import React, { useState, useEffect, useRef } from 'react';
import { generateTexts } from '../utils/ai';
import { createTextsImportString } from '../utils/importFormat';

// Alle Generator-Einstellungen werden im localStorage zwischengespeichert und
// beim erneuten Öffnen der Seite wiederhergestellt (inkl. der bei "Normal"
// ausgeblendeten Charakterfelder Fraktion/Klasse/Rasse und der Textbox).
const STORAGE_KEY = 'rpm_generator_settings';

function loadSettings() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
}

// WoW Classic Klassen/Rassen. Es werden nur die Keys ans Backend geschickt —
// api.php validiert sie gegen eine Whitelist und baut den Prompt serverseitig.
// Anzeige-Labels kommen aus den i18n-Dateien (t.classes / t.races).
const WOW_CLASSES = [
  { key: 'warrior' },
  { key: 'paladin', faction: 'alliance' },
  { key: 'hunter' },
  { key: 'rogue' },
  { key: 'priest' },
  { key: 'shaman', faction: 'horde' },
  { key: 'mage' },
  { key: 'warlock' },
  { key: 'druid' },
];

const WOW_RACES = [
  { key: 'human', faction: 'alliance' },
  { key: 'dwarf', faction: 'alliance' },
  { key: 'nightelf', faction: 'alliance' },
  { key: 'gnome', faction: 'alliance' },
  { key: 'orc', faction: 'horde' },
  { key: 'undead', faction: 'horde' },
  { key: 'tauren', faction: 'horde' },
  { key: 'troll', faction: 'horde' },
];

export default function Generator({ t, lang }) {
  const saved = useRef(loadSettings()).current;
  const [prompt, setPrompt] = useState(saved.prompt || '');
  const [textType, setTextType] = useState(saved.textType || 'say');
  const [count, setCount] = useState(saved.count || 10);
  const [textLang, setTextLang] = useState(saved.textLang || lang);
  const [style, setStyle] = useState(saved.style || 'normal');
  const [textLength, setTextLength] = useState(saved.textLength || 'normal');
  const [faction, setFaction] = useState(saved.faction || '');
  const [wowClass, setWowClass] = useState(saved.wowClass || '');
  const [race, setRace] = useState(saved.race || '');

  // Beim Wechsel der Oberflächensprache folgt die Textsprache — aber NICHT beim
  // ersten Rendern, damit der gespeicherte Wert erhalten bleibt.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setTextLang(lang);
  }, [lang]);

  // Alle Optionen bei jeder Änderung im localStorage sichern (auch die
  // ausgeblendeten Charakterfelder — sie sind beim Wiedereinblenden wieder da).
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      prompt, textType, count, textLang, style, textLength, faction, wowClass, race,
    }));
  }, [prompt, textType, count, textLang, style, textLength, faction, wowClass, race]);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [editValue, setEditValue] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError('');
    setCopied(false);
    try {
      const result = await generateTexts(prompt, count, textLang, textType, {
        style,
        length: textLength,
        faction: style === 'lore' ? faction : '',
        wowClass: style === 'lore' ? wowClass : '',
        race: style === 'lore' ? race : '',
      });
      setMessages(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setCooldown(10);
      cooldownRef.current = setInterval(() => {
        setCooldown(prev => {
          if (prev <= 1) {
            clearInterval(cooldownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  const handleCopy = () => {
    if (messages.length === 0) return;
    const importStr = createTextsImportString(messages);
    navigator.clipboard.writeText(importStr);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = (index) => {
    setMessages(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditStart = (index) => {
    setEditIndex(index);
    setEditValue(messages[index]);
  };

  const handleEditSave = () => {
    if (editIndex >= 0 && editValue.trim()) {
      setMessages(prev => prev.map((msg, i) => i === editIndex ? editValue.trim() : msg));
    }
    setEditIndex(-1);
    setEditValue('');
  };

  // Bei "Lore Echt" automatisch Textlänge "Lang" als Standard vorwählen
  // (kann danach weiterhin manuell geändert werden)
  const handleStyleChange = (newStyle) => {
    setStyle(newStyle);
    if (newStyle === 'lore') {
      setTextLength('long');
    }
  };

  // Nur Klassen/Rassen der gewählten Fraktion anbieten (WoW Classic Lore)
  const availableClasses = WOW_CLASSES.filter(c => !faction || !c.faction || c.faction === faction);
  const availableRaces = WOW_RACES.filter(r => !faction || r.faction === faction);

  const handleFactionChange = (newFaction) => {
    setFaction(newFaction);
    const classDef = WOW_CLASSES.find(c => c.key === wowClass);
    if (newFaction && classDef && classDef.faction && classDef.faction !== newFaction) {
      setWowClass('');
    }
    const raceDef = WOW_RACES.find(r => r.key === race);
    if (newFaction && raceDef && raceDef.faction !== newFaction) {
      setRace('');
    }
  };

  // Umschalt-Gruppen als Segmented Controls (einheitlich ausgerichtet)
  const segGroups = [
    {
      label: t.textTypeLabel,
      value: textType,
      set: setTextType,
      options: [
        { key: 'say', label: t.textTypeSay },
        { key: 'emote', label: t.textTypeEmote },
      ],
    },
    {
      label: t.styleLabel,
      value: style,
      set: handleStyleChange,
      options: [
        { key: 'normal', label: t.styleNormal },
        { key: 'lore', label: t.styleLore },
      ],
    },
    {
      label: t.lengthLabel,
      value: textLength,
      set: setTextLength,
      options: [
        { key: 'normal', label: t.lengthNormal },
        { key: 'long', label: t.lengthLong },
      ],
    },
  ];

  return (
    <div>
      <div className="seg-row">
        {segGroups.map(group => (
          <div className="seg-group" key={group.label}>
            <label className="wow-label">{group.label}</label>
            <div className="seg" role="radiogroup" aria-label={group.label}>
              {group.options.map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  role="radio"
                  aria-checked={group.value === opt.key}
                  className={`seg-option ${group.value === opt.key ? 'active' : ''}`}
                  onClick={() => group.set(opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {style === 'lore' && (
        <div style={{ marginBottom: '16px' }}>
          <label className="wow-label">{t.characterLabel}</label>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <select
              className="wow-input"
              style={{ width: '160px' }}
              value={faction}
              onChange={e => handleFactionChange(e.target.value)}
            >
              <option value="">{t.factionPlaceholder}</option>
              <option value="alliance">{t.factions.alliance}</option>
              <option value="horde">{t.factions.horde}</option>
            </select>
            <select
              className="wow-input"
              style={{ width: '160px' }}
              value={wowClass}
              onChange={e => setWowClass(e.target.value)}
            >
              <option value="">{t.classPlaceholder}</option>
              {availableClasses.map(({ key }) => (
                <option key={key} value={key}>{t.classes[key] || key}</option>
              ))}
            </select>
            <select
              className="wow-input"
              style={{ width: '160px' }}
              value={race}
              onChange={e => setRace(e.target.value)}
            >
              <option value="">{t.racePlaceholder}</option>
              {availableRaces.map(({ key }) => (
                <option key={key} value={key}>{t.races[key] || key}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <label className="wow-label">{t.promptLabel}</label>
        <textarea
          className="wow-textarea"
          rows={3}
          maxLength={500}
          placeholder={t.promptPlaceholder}
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
        />
      </div>

      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div>
          <label className="wow-label">{t.countLabel}</label>
          <select
            className="wow-input"
            style={{ width: '80px' }}
            value={count}
            onChange={e => setCount(parseInt(e.target.value))}
          >
            {[1,2,3,4,5,6,7,8,9,10,15,20,25,50].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="wow-label">{t.languageLabel}</label>
          <select
            className="wow-input"
            style={{ width: '140px' }}
            value={textLang}
            onChange={e => setTextLang(e.target.value)}
          >
            {Object.entries(t.languages).map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </div>
        <button
          className="wow-btn wow-btn-primary"
          onClick={handleGenerate}
          disabled={loading || !prompt.trim() || cooldown > 0}
        >
          {loading ? t.generating : cooldown > 0 ? `${t.generateBtn} (${cooldown}s)` : t.generateBtn}
        </button>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div className="wow-loading" style={{ fontSize: '18px', marginBottom: '8px' }}>&#9881; {t.generating}</div>
          <div style={{ color: 'var(--wow-text-muted)', fontSize: '13px' }}>This may take a few seconds...</div>
        </div>
      )}

      {error && <div className="wow-error">{error}</div>}

      <div className="wow-separator" />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <label className="wow-label" style={{ marginBottom: 0 }}>{t.resultLabel} ({messages.length})</label>
        {messages.length > 0 && (
          <button className="wow-btn" onClick={handleCopy}>
            {copied ? `✓ ${t.copied}` : t.copyImportBtn}
          </button>
        )}
      </div>

      {messages.length === 0 ? (
        <p style={{ color: 'var(--wow-text-muted)' }}>{t.noTexts}</p>
      ) : (
        <div>
          {messages.map((msg, index) => (
            <div key={index} className="wow-text-item">
              <span style={{ color: 'var(--wow-text-muted)', minWidth: '28px', fontSize: '12px' }}>
                #{index + 1}
              </span>
              {editIndex === index ? (
                <input
                  className="wow-input"
                  style={{ flex: 1 }}
                  value={editValue}
                  maxLength={240}
                  onChange={e => setEditValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleEditSave(); if (e.key === 'Escape') setEditIndex(-1); }}
                  autoFocus
                />
              ) : (
                <span style={{ flex: 1, fontSize: '14px', color: 'var(--wow-text)' }}>{msg}</span>
              )}
              <div style={{ display: 'flex', gap: '4px' }}>
                {editIndex === index ? (
                  <button className="wow-btn" style={{ padding: '2px 8px', fontSize: '12px' }} onClick={handleEditSave}>✓</button>
                ) : (
                  <button className="wow-btn" style={{ padding: '2px 8px', fontSize: '12px' }} onClick={() => handleEditStart(index)} title={t.editTooltip}>✎</button>
                )}
                <button className="wow-btn wow-btn-danger" style={{ padding: '2px 8px', fontSize: '12px' }} onClick={() => handleDelete(index)} title={t.deleteTooltip}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
